import { useMemo } from 'react';
import * as yup from 'yup';
import i18n from 'helpers/i18n';
import messages from './messages';
import useCurrentUser from 'hooks/useCurrentUser';
import useIsCurrentUserAdministrator from '../hooks/useIsCurrentUserAdministrator';
import { clsx } from 'clsx';
import { usePrimaryAdminHierarchySync } from 'components/Organization/Hierarchy/utils';
import { roleToText } from 'helpers/role';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import { addUserToAccount } from 'actions/accounts';
import { useGetManageableAccountSubscriptions } from 'actions/subscriptions';
import { useGetClientAccountRoles } from 'actions/roles';
import { useAccountUserContext } from '../AccountUserContext';
import {
  SubscriptionSeatsField,
  AccountSubscriptionOption,
  SubscriptionSeatFormValue
} from './SubscriptionSeatsField';
import useIsPrimaryAdmin from '../hooks/useIsPrimaryAdmin';
import { inactiveRolesFilter } from 'helpers/role/utils';
import { toastSuccess } from 'components/Organization/Toast';
import { IconButton } from 'components/Organization/IconButton';
import { Select, SelectOption } from 'components/Organization/Select';
import { PrimaryButton } from 'components/Organization/PrimaryButton';
import { HierarchyBuilder } from 'components/Organization/Hierarchy/utils';
import PageLoader from 'components/PageLoader';
import { TextField } from 'components/TextField';

import {
  useForm,
  Controller,
  useFieldArray,
  FormProvider,
  useFormContext
} from 'react-hook-form';

import {
  HierarchyInput,
  PrimaryAdminHierarchyDisplay,
  useAccountHierarchy
} from 'components/Organization/Hierarchy';
import RoutesHelper from 'helpers/routes';
import { subscriptionRequiresRole } from '../subscriptionProductRoles';

function parseError(error: any) {
  return error?.response?.data ?? [{}];
}

type RoleSelectionOption = SelectOption & { name: string };

const ALLOWED_SUBSCRIPTIONS = [
  'learning_resources',
  'data_collection',
  'class_connect',
  'instant_insights'
];

interface MemberFormProps {
  index: number;
  roles: RoleSelectionOption[];
  remove?: () => void;
  hierarchy: HierarchyBuilder;
  requiredHierarchyAssociation: boolean;
  subscriptions: AccountSubscriptionOption[];
}

function MemberForm({
  index,
  roles,
  remove,
  hierarchy,
  requiredHierarchyAssociation,
  subscriptions
}: MemberFormProps) {
  const {
    control,
    register,
    formState: { errors, isSubmitted },
    setValue,
    watch
  } = useFormContext();

  const primaryAdminRole = roles.find(r => /primary admin/i.test(r.text));
  const primaryAdminRoleId = primaryAdminRole?.value;
  const selectedRoleId = watch(`members.${index}.role_id`);
  const isPrimaryAdmin = Number(selectedRoleId) === Number(primaryAdminRoleId);
  const nodeIds = watch(`members.${index}.node_ids`);
  const subscriptionSeats: SubscriptionSeatFormValue[] =
    watch(`members.${index}.subscription_seats`) ?? [];
  const rootNodeId = hierarchy.nodes?.[0].id;
  const subscriptionSeatsErrors = (errors.members as any)?.[index]
    ?.subscription_seats;
  const subscriptionApiError =
    typeof subscriptionSeatsErrors?.message === 'string'
      ? subscriptionSeatsErrors.message
      : undefined;

  function getSubscriptionRoleError(subscriptionId: number) {
    if (!Array.isArray(subscriptionSeatsErrors)) {
      return undefined;
    }

    const seatIndex = subscriptionSeats.findIndex(
      seat => seat.subscription_id === subscriptionId
    );

    if (seatIndex < 0) {
      return undefined;
    }

    return subscriptionSeatsErrors[seatIndex]?.role?.message as
      | string
      | undefined;
  }

  const subscriptionRoleErrors = isSubmitted
    ? subscriptionSeats.reduce<Record<number, string>>((acc, seat) => {
        const message = getSubscriptionRoleError(seat.subscription_id);

        if (message) {
          acc[seat.subscription_id] = message;
        }

        return acc;
      }, {})
    : {};

  usePrimaryAdminHierarchySync({
    isPrimaryAdmin,
    selectedRoleId,
    rootNodeId,
    nodeIds,
    setNodeIds: (ids: number[]) => setValue(`members.${index}.node_ids`, ids)
  });

  function setMemberRole(roleId: number) {
    setValue(`members.${index}.role_id`, roleId, { shouldValidate: true });
    if (Number(roleId) === Number(primaryAdminRoleId)) {
      if (rootNodeId) {
        setValue(`members.${index}.node_ids`, [rootNodeId], {
          shouldValidate: true
        });
      }
    }
  }

  return (
    <section>
      {errors.root?.[index] && (
        <div className="font-sans font-semibold text-base text-[#3C3F42] p-4 bg-[#EED3D8] border-x border-t border-[#AC213A] rounded-t-2xl flex items-center gap-2">
          <i className="fa-solid fa-circle-xmark text-[#AC213A]" />
          <span>{errors?.root?.[index]?.message}</span>
        </div>
      )}
      <div
        className={clsx(
          'bg-white p-6 rounded-2xl',
          errors.root?.[index] &&
            'rounded-t-none border-b border-x border-[#AC213A]'
        )}
      >
        {remove && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={remove}
              className="text-action text-sm font-sans font-semibold"
            >
              {i18n.ft(messages.removeMember)}
              <span className="fa fa-trash-can fa-solid pl-2"></span>
            </button>
          </div>
        )}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <TextField
            {...register(`members.${index}.email`)}
            required
            label={i18n.ft(messages.fields.email)}
            placeholder={i18n.ft(messages.fields.emailPlaceholder)}
            error={(errors.members as any)?.[index]?.email?.message}
          />
          <TextField
            {...register(`members.${index}.first_name`)}
            required
            label={i18n.ft(messages.fields.firstName)}
            placeholder={i18n.ft(messages.fields.firstNamePlaceholder)}
            error={(errors.members as any)?.[index]?.first_name?.message}
          />
          <TextField
            {...register(`members.${index}.last_name`)}
            required
            label={i18n.ft(messages.fields.lastName)}
            placeholder={i18n.ft(messages.fields.lastNamePlaceholder)}
            error={(errors.members as any)?.[index]?.last_name?.message}
          />
          <Controller
            name={`members.${index}.role_id`}
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                label={i18n.ft(messages.fields.role)}
                placeholder={i18n.ft(messages.fields.rolePlaceholder)}
                onChange={setMemberRole}
                options={roles.filter(a => inactiveRolesFilter(a))}
              />
            )}
          />
          <div className="sm:col-span-2">
            {isPrimaryAdmin ? (
              <PrimaryAdminHierarchyDisplay name={hierarchy.nodes?.[0].name} />
            ) : (
              <Controller
                name={`members.${index}.node_ids`}
                control={control}
                render={({ field }) => (
                  <HierarchyInput
                    {...hierarchy}
                    value={field.value}
                    onChange={value =>
                      setValue(`members.${index}.node_ids`, value)
                    }
                    label={i18n.ft(messages.fields.hierarchy)}
                    placeholder={i18n.ft(messages.fields.hierarchyPlaceholder)}
                    error={(errors.members as any)?.[index]?.node_ids?.message}
                    required={requiredHierarchyAssociation}
                  />
                )}
              />
            )}
          </div>
          {subscriptions.length > 0 && (
            <div className="sm:col-span-2 lg:col-span-3">
              <p className="font-sans font-semibold text-black text-base mb-2">
                {i18n.ft(messages.fields.subscriptions)}
              </p>
              <SubscriptionSeatsField
                subscriptions={subscriptions}
                value={subscriptionSeats}
                roleErrors={subscriptionRoleErrors}
                apiError={subscriptionApiError}
                onChange={seats =>
                  setValue(`members.${index}.subscription_seats`, seats, {
                    shouldValidate: isSubmitted
                  })
                }
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

interface AddMembersProps {
  /** Current account id. */
  accountId: number;

  /** List of roles formatted for select component. */
  roles: RoleSelectionOption[];

  /** Hierarchy association is required when the current user is an account administrator. */
  requiredHierarchyAssociation: boolean;

  /** Subscriptions available for seat assignment. */
  subscriptions: AccountSubscriptionOption[];
}

function createAddMembersSchema(
  requiredHierarchyAssociation: boolean,
  administratorRoleId: number | null | undefined,
  subscriptions: AccountSubscriptionOption[]
) {
  return yup.object({
    members: yup.array().of(
      yup.object({
        email: yup
          .string()
          .email(i18n.ft(messages.errors.email))
          .required(i18n.ft(messages.errors.required)),
        first_name: yup.string().required(i18n.ft(messages.errors.required)),
        last_name: yup.string().required(i18n.ft(messages.errors.required)),
        role_id: yup.number().nullable(),
        node_ids: yup
          .array()
          .of(yup.number().required())
          .when([], {
            is: () => requiredHierarchyAssociation,
            then: schema =>
              schema
                .min(1, i18n.ft(messages.errors.required))
                .required(i18n.ft(messages.errors.required)),
            otherwise: schema => schema
          })
          .when('role_id', {
            is: (role_id: number) => Number(role_id) === administratorRoleId,
            then: schema =>
              schema
                .min(1, i18n.ft(messages.errors.adminHierarchyRequired))
                .required(),
            otherwise: schema => schema
          }),
        subscription_seats: yup
          .array()
          .of(
            yup.object({
              subscription_id: yup.number().required(),
              role: yup
                .string()
                .default('')
                .test('subscription-role-required', function (value) {
                  const subscriptionId = this.parent.subscription_id;
                  const subscription = subscriptions.find(
                    sub => sub.id === subscriptionId
                  );

                  if (
                    !subscription ||
                    !subscriptionRequiresRole(subscription.product.name) ||
                    value
                  ) {
                    return true;
                  }

                  const name =
                    subscription.name || subscription.product.display_name;

                  return this.createError({
                    message: i18n.ft(
                      messages.errors.subscriptionRoleRequiredFor,
                      { name }
                    )
                  });
                })
            })
          )
          .default([])
      })
    )
  });
}

function AddMembers({
  accountId,
  roles,
  requiredHierarchyAssociation,
  subscriptions
}: AddMembersProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    email,
    name,
    subscriptionId,
    existingSubscriptionMember,
    fromSubscriptionInvite
  } = location.state || {};
  const hierarchy = useAccountHierarchy(accountId);

  const administratorRole = roles.find(r => /administrator/i.test(r.text));
  const administratorRoleId = administratorRole?.value;

  const schema = useMemo(
    () =>
      createAddMembersSchema(
        requiredHierarchyAssociation,
        administratorRoleId != null ? Number(administratorRoleId) : undefined,
        subscriptions
      ),
    [requiredHierarchyAssociation, administratorRoleId, subscriptions]
  );

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      members: [
        {
          email: email || '',
          first_name: name ? name.split(' ')[0] : '',
          last_name: name ? name.split(' ').slice(1).join(' ') : '',
          node_ids: [],
          subscription_seats: subscriptionId
            ? [{ subscription_id: subscriptionId, role: '' }]
            : []
        }
      ]
    }
  });

  const { control, handleSubmit, setError } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'members'
  });

  const { isPending, mutate: addUser } = useMutation({
    mutationFn: addUserToAccount,
    onSuccess: ({ data }) => {
      if (subscriptionId && fromSubscriptionInvite) {
        navigate(
          RoutesHelper.getPath('organization-subscriptions-invite', {
            id: subscriptionId
          }),
          { state: { email } }
        );
        toastSuccess({
          message: i18n.ft(messages.userSubscriptionInviteAllowed)
        });
      } else if (subscriptionId && existingSubscriptionMember) {
        navigate(
          RoutesHelper.getPath('organization-subscriptions-users', {
            id: subscriptionId
          }),
          { state: { email, fromInviteForm: true } }
        );
        toastSuccess({
          message: i18n.ft(messages.userSubscriptionMember, {
            email: email
          })
        });
      } else {
        navigate('/organization');
        toastSuccess({
          message: i18n.ft(messages.usersAdded, { count: data.length })
        });
      }
    },
    onError: error => {
      const errors = parseError(error);

      errors.forEach((userError: any, index: number) => {
        if (userError) {
          if (userError.email) {
            setError(`members.${index}.email`, { message: userError.email });
          }

          if (userError.first_name) {
            setError(`members.${index}.first_name`, {
              message: userError.first_name
            });
          }

          if (userError.last_name) {
            setError(`members.${index}.last_name`, {
              message: userError.last_name
            });
          }

          if (userError.account_user) {
            setError(`root.${index}`, { message: userError.account_user });
          }

          if (userError.node_ids) {
            setError(`members.${index}.node_ids`, {
              message: userError.node_ids
            });
          }

          const subscriptionFieldError = userError.subscription_seats;

          if (subscriptionFieldError) {
            const message = Array.isArray(subscriptionFieldError)
              ? subscriptionFieldError.join(', ')
              : subscriptionFieldError;

            setError(`members.${index}.subscription_seats`, {
              message
            });
          }
        }
      });
    }
  });

  const handleFormSubmit = (data: any) => {
    if (isPending) {
      return;
    }
    addUser({ accountId, params: { users: data.members } });
  };

  return (
    <FormProvider {...methods}>
      <div className="px-4 md:px-6 xl:px-10 max-w-7xl mx-auto">
        <div className="flex items-center gap-2 pt-12 pb-4">
          <IconButton
            label={i18n.ft(messages.goBack)}
            icon="fa-solid fa-arrow-left"
            onClick={() => navigate(-1)}
          />

          <h2 className="font-sans text-2xl sm:text-3xl">
            {i18n.ft(messages.addMember)}
          </h2>
        </div>

        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          className="flex flex-col gap-4"
        >
          {fields.map((item, index) => (
            <MemberForm
              key={item.id}
              index={index}
              roles={roles}
              remove={index !== 0 ? () => remove(index) : undefined}
              hierarchy={hierarchy}
              requiredHierarchyAssociation={requiredHierarchyAssociation}
              subscriptions={subscriptions}
            />
          ))}

          <div className="flex justify-between">
            <button
              type="button"
              disabled={isPending}
              className="cursor-pointer text-action font-sans text-sm font-semibold py-4 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={() =>
                append({
                  email: '',
                  first_name: '',
                  last_name: '',
                  role_id: null,
                  node_ids: [],
                  subscription_seats: subscriptionId
                    ? [{ subscription_id: subscriptionId, role: '' }]
                    : []
                })
              }
            >
              <span className="fa fa-plus fa-solid pr-2"></span>
              {i18n.ft(messages.addAnotherMember)}
            </button>
            <div className="py-4 flex justify-end">
              <PrimaryButton type="submit" disabled={isPending}>
                {isPending
                  ? i18n.ft(messages.addingMember)
                  : i18n.ft(messages.addMember)}
              </PrimaryButton>
            </div>
          </div>
        </form>
      </div>
    </FormProvider>
  );
}

function AddMembersController() {
  const currentUser = useCurrentUser();
  const accountId = currentUser.current_account_id ?? 0;
  const isCurrentUserAccountAdministrator = useIsCurrentUserAdministrator();
  const { roles: accountRoles, settings } = useAccountUserContext();
  const isPrimaryAdmin = useIsPrimaryAdmin(accountRoles);
  const canAssignSubscriptions =
    isPrimaryAdmin ||
    !Boolean(settings?.restrict_subscription_management_to_ca_admin);

  const { data, isSuccess } = useGetClientAccountRoles(accountId);
  const { data: subscriptionsData, isSuccess: subscriptionsLoaded } =
    useGetManageableAccountSubscriptions();

  if (!isSuccess || !subscriptionsLoaded) {
    return <PageLoader />;
  }

  const roles = data.map(role => ({
    value: role.id,
    text: roleToText(role.name),
    name: role.name
  }));

  const subscriptions = canAssignSubscriptions
    ? (subscriptionsData?.subscriptions ?? []).filter(subscription =>
        ALLOWED_SUBSCRIPTIONS.includes(subscription.product.name)
      )
    : [];

  return (
    <AddMembers
      accountId={accountId}
      roles={roles}
      requiredHierarchyAssociation={isCurrentUserAccountAdministrator}
      subscriptions={subscriptions}
    />
  );
}

export default AddMembersController;
